import { computed, reactive } from 'vue'
import { defineStore } from 'vue_features/shared/composables/store_helpers.js'

const uniqueBy = (list = [], uniquenessIdentifier = (a) => a) => {
  return Object.values(
    list.reduce((uniqueItems, item) => {
      uniqueItems[uniquenessIdentifier(item)] ||= item
      return uniqueItems
    }, {}),
  )
}

// Standards starting with the prefix 'BI' are considered "big idea", unit
// level standards for California.  For the sake of supporting similar future
// unit level standards, this is built out to support a list of big idea indicators.
// These are typically standards that are tagged to the lesson level (since this is
// the only way our uploader allows) but are actually only valid across the unit and
// are not completely valid at the lesson level. In the future, an authoring solution
// providing a way to mark 'unit level' standards may be more appropriate.
const unitLevelPrefixes = ['BI']
const unitLevelRegex = new RegExp(`^(${unitLevelPrefixes.join('|')})\\.`)

export const useLessonPlanStandardsStore = defineStore('lessonPlanStandards', () => {
  const state = reactive({
    standards: [],
    customerVisibleStandards: [],
  })

  const allStandards = computed(() => uniqueBy(state.standards, (standard) => standard.displayCode))

  const allCustomerVisibleStandards = computed(() =>
    uniqueBy(state.customerVisibleStandards, (standard) => standard.displayCode),
  )

  const matchesUnitLevelRegex = (standard) => unitLevelRegex.test(standard.displayCode)

  const groupedStandards = computed(() =>
    allCustomerVisibleStandards.value.reduce(
      (groups, standard) => {
        const group = matchesUnitLevelRegex(standard) ? 'unitLevel' : 'lessonLevel'
        groups[group].push(standard)
        return groups
      },
      {
        unitLevel: [],
        lessonLevel: [],
      },
    ),
  )

  const updateAllStandards = (newStandards) => {
    state.standards = newStandards
  }

  const updateCustomerVisibleStandards = (newCustomerVisibleStandards) => {
    state.customerVisibleStandards = newCustomerVisibleStandards
  }

  return {
    state,
    allStandards,
    allCustomerVisibleStandards,
    unitLevelStandards: computed(() => groupedStandards.value.unitLevel),
    lessonLevelStandards: computed(() => groupedStandards.value.lessonLevel),
    updateAllStandards,
    updateCustomerVisibleStandards,
  }
})
